<template>
  <div
    :class="[
      'sidebar-main',
      `sidebar-main_${isActive}`,
      `sidebar-main-animation_${clickStatus}`,
    ]"
  >
    <div class="sidebar-main__header">
      <h4
        :class="[
          'sidebar-main__header-text',
          `sidebar-main__header-text_${isActive}`,
        ]"
      >
        Разделы
      </h4>
      <IconButton
        v-if="hasSidebarHandleBtn"
        value="arrow-reveal"
        :ui="{
          button: 'sidebar-main__header-button',
          icon: `sidebar-main__header-icon_${isActive}`,
          tooltip: { anchor: 'sidebar-main__header-tooltip-anchor' },
        }"
        @click="handleSidebarHeader"
      />
    </div>
    <div class="sidebar-main__main">
      <div class="sidebar-main__main-items">
        <template v-if="store.isInited">
          <Tooltip
            v-for="(item, idx) in treeToUse"
            :key="idx"
            :text="item.title"
            scrolling
            :offset="[15, 15]"
            :position="'left'"
            :disabled="!isRollup"
            :ui="{ anchor: 'sidebar-main__tooltip' }"
          >
            <component
              :is="item.to.name === route.name ? 'div' : 'router-link'"
              :to="item.to.name === route.name ? undefined : item.to"
              :class="[
                'sidebar-main__item',
                { 'sidebar-main__item_passive': isRollup },
                { 'sidebar-main__item_active-link': item.isActive },
              ]"
            >
              <div class="sidebar-main__item-icon">
                <div class="sidebar-main__item-icon__i">
                  <Icon :value="item.icon" />
                  <div
                    v-if="item.to.name === EPGU_LIST_NAME && hasEpgu"
                    class="sidebar-main__item-icon__i-notification"
                  />
                </div>
              </div>
              <h5 class="sidebar-main__item-text">{{ item.title }}</h5>
            </component>
          </Tooltip>
        </template>
      </div>

      <EpkLinks class="sidebar__links" :not-show-hint="isActive" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { EPGU_LIST_NAME } from '~packages/epk-front-router/routes.json';
import { Icon, IconButton } from '~packages/epk-front-shared/ui';

import type { IDefaultSidebarGroup } from '@/components/Layouts/EpkSidebar/types';
import getConstants from '@/constants';
import { checkNotificationEpgu } from '@/helpers/checkNotificationEpgu';
import { useOrgStore } from '@/shared/model';
import { Tooltip } from '@/shared/ui';
import { useModalNotification, useRightStore, useSidebar } from '@/store';
import { useWindowSize } from '@vueuse/core';

import { EpkLinks } from './EpkLinks';
import listRouteTreeGet from './list-route-tree-get';

const props = defineProps({
  sidebarType: { type: String, default: '1' },
});

const { appBasePath } = getConstants();

const orgStore = useOrgStore();

const modalNotification = useModalNotification();
const notificationEpgu = checkNotificationEpgu();

const sidebar = useSidebar();
const store = useRightStore();
const route = useRoute();
const { width: windowWidth } = useWindowSize();

const clickStatus = ref();
const period = ref(Number(route.query.period as unknown as number));

const hasSidebarHandleBtn = computed(() => {
  if (props.sidebarType === '1') return true;

  if (sidebar.isOpenSidebar || windowWidth.value > 1440) return true;

  return false;
});

const isActive = computed(() => (sidebar.isOpenSidebar ? 'active' : 'passive'));

const hasEpgu = computed(() => {
  return notificationEpgu?.epgu.hasEpgu && route.name !== EPGU_LIST_NAME;
});

const isRollup = computed(() => isActive.value === 'passive');

const treeBase = computed(() => {
  if (orgStore.userInfo.approved_roles.find(i => i === 'admin'))
    return listRouteTreeGet(true) as IDefaultSidebarGroup[];
  return listRouteTreeGet(store.hasRightWrite) as IDefaultSidebarGroup[];
});

const treeToUse = computed(() => {
  const routePath = route.path;

  return treeBase.value.map(item => {
    const children = item.children?.map(child => ({
      ...child,
      isActive:
        routePath.startsWith(child.path) || period.value == child.period,
    }));
    const itemBasePath =
      appBasePath === '/' ? item.path : appBasePath.slice(0, -1) + item.path;

    return {
      ...item,
      children,
      isActive:
        routePath.startsWith(itemBasePath) || children?.find(i => i.isActive),
    };
  });
});

const handleSidebarHeader = () => {
  sidebar.isOpenSidebar = !sidebar.isOpenSidebar;
  clickStatus.value = isActive.value;
};

onBeforeRouteLeave(to => {
  modalNotification.checkChanges();
  return !(modalNotification.isModalActive || route.name === to.name);
});

watch(
  () => route.query.period,
  newPeriod => {
    period.value = Number(newPeriod as unknown as number);
  }
);
watch(
  () => route,
  () => {
    if (!notificationEpgu?.epgu.epguPage && route.name === EPGU_LIST_NAME) {
      notificationEpgu.epgu.epguPage = true;
      clearTimeout(notificationEpgu.epgu.timerId);
    } else if (notificationEpgu?.epgu.epguPage) {
      delete notificationEpgu.epgu.epguPage;
      notificationEpgu.init();
    }
  },
  { deep: true }
);
</script>

<style lang="scss">
@import './EpkSidebarMain';
</style>
